'use client';

import { ErrorBoundary } from '@/components/error-boundary';
import { SecretCatDialog } from '@/components/secret-cat-dialog';
import { GlobalCommandMenu } from '@/components/command-menu/global-command-menu';
import type { MenuPage } from '@/components/command-menu/types';
import { GlobalShortcuts } from '@/components/global-shortcuts';
import { SecretImageDialog } from '@/components/secret-image-dialog';
import { SecretPunDialog } from '@/components/secret-pun-dialog';
import { api, useQuery } from '@/lib/convex';
import { isEditableShortcutTarget } from '@/lib/utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type CommandMenuOpenOptions = {
  query?: string;
  page?: MenuPage;
};

type CommandMenuContextType = {
  isOpen: boolean;
  open: (options?: CommandMenuOpenOptions) => void;
  close: () => void;
  toggle: () => void;
  query: string;
  setQuery: (value: string) => void;
  /** Sub-page to land on when the menu opens; consumed by the menu on open. */
  initialPage: MenuPage | null;
};

const CommandMenuContext = createContext<CommandMenuContextType | undefined>(
  undefined
);

export function CommandMenuProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [initialPage, setInitialPage] = useState<MenuPage | null>(null);
  const { data: viewer } = useQuery(api.users.queries.viewer);

  const open = useCallback((options?: CommandMenuOpenOptions) => {
    if (options?.query !== undefined) {
      setQuery(options.query);
    }
    setInitialPage(options?.page ?? null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setInitialPage(null);
  }, []);
  // Bumped only after a menu crash, so the boundary remounts clean.
  const [menuBoundaryKey, setMenuBoundaryKey] = useState(0);
  const handleMenuCrash = useCallback(() => {
    close();
    setMenuBoundaryKey((key) => key + 1);
  }, [close]);
  const toggle = useCallback(() => {
    setIsOpen((v) => {
      const next = !v;
      if (!next) {
        setQuery('');
      }
      setInitialPage(null);
      return next;
    });
  }, []);

  // Cmd+K / Ctrl+K should toggle even while the command input is focused.
  useEffect(() => {
    if (!viewer) {
      return undefined;
    }

    const handler = (e: KeyboardEvent) => {
      const isShortcut =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';

      if (!isShortcut || e.repeat) {
        return;
      }

      if (isEditableShortcutTarget(e.target, { allowCommandInput: true })) {
        return;
      }

      e.preventDefault();
      toggle();
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [toggle, viewer]);

  useEffect(() => {
    if (!viewer && isOpen) {
      setTimeout(() => {
        close();
      }, 0);
    }
  }, [viewer, isOpen, close]);

  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      query,
      setQuery,
      initialPage,
    }),
    [isOpen, open, close, toggle, query, initialPage]
  );

  return (
    <CommandMenuContext.Provider value={value}>
      {children}
      {/* A failing menu query closes the menu instead of crashing the page. */}
      <ErrorBoundary
        key={menuBoundaryKey}
        fallback={<></>}
        onError={handleMenuCrash}
      >
        <GlobalCommandMenu />
      </ErrorBoundary>
      <GlobalShortcuts />
      <SecretCatDialog />
      <SecretImageDialog />
      <SecretPunDialog />
    </CommandMenuContext.Provider>
  );
}

export function useCommandMenu() {
  const ctx = useContext(CommandMenuContext);
  if (!ctx) {
    throw new Error('useCommandMenu must be used within a CommandMenuProvider');
  }
  return ctx;
}
